import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import ConfirmSheet from '@/components/ConfirmSheet';
import Avatar from '@/components/Avatar';
import { Users, Plus, UserPlus, Mail, Phone, MapPin, ShieldCheck, ShieldOff, Trash2, Sprout } from 'lucide-react';
import { toast } from 'sonner';

function FarmerRow({ farmer, onUpdate, onUnlink, busy }) {
  const isFarmer = farmer.role === 'farmer';
  const isSuspended = farmer.org_status === 'suspended';

  return (
    <div
      data-testid={`org-farmer-row-${farmer.id}`}
      className={`bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-3 flex items-center gap-3 ${isSuspended ? 'opacity-60' : ''}`}
    >
      <Avatar name={farmer.name} src={farmer.avatar} size={40} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#0B1620] truncate flex items-center gap-1">
          {farmer.name}
          {isFarmer && <Sprout className="w-3 h-3 text-[#15803D]" />}
          {isSuspended && <span className="text-[9px] text-[#B91C1C] font-bold ml-1">SUSPENDED</span>}
        </p>
        <p className="text-xs text-[#5B6675] truncate">{farmer.phone}</p>
        {farmer.location && (
          <p className="text-[10px] text-[#5B6675]/80 truncate flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{farmer.location}</p>
        )}
        {typeof farmer.post_count === 'number' && (
          <p className="text-[10px] text-[#5B6675]/80 mt-0.5">{farmer.post_count} produce post(s)</p>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <button
          data-testid={`org-farmer-toggle-${farmer.id}`}
          onClick={() => onUpdate(farmer.id, { status: isSuspended ? 'active' : 'suspended' })}
          disabled={busy}
          className={`text-[10px] px-2 py-1 rounded-lg font-semibold flex items-center gap-1 ${
            isSuspended ? 'text-[#15803D] bg-[#15803D]/10' : 'text-[#B91C1C] bg-[#B91C1C]/10'
          }`}
        >
          {isSuspended ? <><ShieldCheck className="w-3 h-3" />Activate</> : <><ShieldOff className="w-3 h-3" />Suspend</>}
        </button>
        <button
          data-testid={`org-farmer-unlink-${farmer.id}`}
          onClick={() => onUnlink(farmer.id)}
          disabled={busy}
          className="text-[10px] px-2 py-1 rounded-lg font-semibold text-[#5B6675] bg-[#EFF2F7] flex items-center gap-1"
        >
          <Trash2 className="w-3 h-3" />Unlink
        </button>
      </div>
    </div>
  );
}

export default function OrgFarmers() {
  const { api } = useAuth();
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [invitePhone, setInvitePhone] = useState('');
  const [createForm, setCreateForm] = useState({ phone: '', name: '', location: '' });
  const [submitting, setSubmitting] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/orgs/me/farmers');
      setFarmers(data.farmers || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load farmers');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post('/orgs/me/farmers/invite', { phone: invitePhone.trim() });
      toast.success(data.message);
      setInvitePhone('');
      setInviteOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/orgs/me/farmers/create', createForm);
      toast.success('Farmer added!');
      setCreateForm({ phone: '', name: '', location: '' });
      setCreateOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (id, updates) => {
    setBusyId(id);
    try {
      await api.put(`/orgs/me/farmers/${id}`, updates);
      toast.success('Updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleUnlink = async () => {
    if (!unlinkTarget) return;
    setBusyId(unlinkTarget.id);
    try {
      await api.delete(`/orgs/me/farmers/${unlinkTarget.id}`);
      toast.success('Unlinked');
      setUnlinkTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unlink failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="org-farmers-section">
      <div className="flex gap-2">
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button data-testid="org-invite-btn" className="flex-1 h-10 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full text-xs font-semibold">
              <Mail className="w-3.5 h-3.5 mr-1" /> Invite by phone
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#F6F8FB]">
            <DialogHeader><DialogTitle>Invite a farmer</DialogTitle></DialogHeader>
            <form onSubmit={handleInvite} className="space-y-3">
              <Label className="text-xs">Phone number</Label>
              <Input
                data-testid="org-invite-phone-input"
                value={invitePhone}
                onChange={(e) => setInvitePhone(e.target.value)}
                placeholder="+919876543210"
                className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm"
              />
              <Button data-testid="org-invite-submit" type="submit" disabled={!invitePhone.trim() || submitting} className="w-full bg-[#0B4F3F] text-[#F6F8FB] rounded-full">
                {submitting ? 'Sending…' : 'Send invite'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="org-create-farmer-btn" variant="outline" className="flex-1 h-10 border-[#0B4F3F] text-[#0B4F3F] rounded-full text-xs font-semibold">
              <UserPlus className="w-3.5 h-3.5 mr-1" /> Add directly
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#F6F8FB]">
            <DialogHeader><DialogTitle>Add a farmer directly</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <Label className="text-xs">Phone</Label>
                <Input
                  data-testid="org-create-phone-input"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  placeholder="+919876543210"
                  className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Name</Label>
                <Input
                  data-testid="org-create-name-input"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="Farmer's full name"
                  className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Location (optional)</Label>
                <Input
                  value={createForm.location}
                  onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
                  placeholder="Village, District"
                  className="h-10 bg-[#F6F8FB] border-[#E4E8EF] rounded-lg text-sm"
                />
              </div>
              <Button data-testid="org-create-submit" type="submit" disabled={!createForm.phone.trim() || !createForm.name.trim() || submitting} className="w-full bg-[#0B4F3F] text-[#F6F8FB] rounded-full">
                {submitting ? 'Adding…' : 'Add farmer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-[#F6F8FB] rounded-xl border border-[#E4E8EF] p-4 animate-pulse h-16" />
        ))
      ) : farmers.length === 0 ? (
        <div className="text-center py-12 bg-[#F6F8FB] rounded-xl border border-[#E4E8EF]">
          <Users className="w-10 h-10 mx-auto text-[#5B6675]/30 mb-3" />
          <p className="text-sm text-[#5B6675]">No members yet</p>
          <p className="text-[11px] text-[#5B6675]/70 mt-1">Invite farmers above to get started</p>
        </div>
      ) : (
        farmers.map((f) => (
          <FarmerRow key={f.id} farmer={f} onUpdate={handleUpdate} onUnlink={(id) => setUnlinkTarget(f)} busy={busyId === f.id} />
        ))
      )}
      <ConfirmSheet
        open={!!unlinkTarget}
        onOpenChange={(o) => !o && setUnlinkTarget(null)}
        title="Unlink this farmer?"
        description={unlinkTarget ? `${unlinkTarget.name} will no longer be part of your organization. They keep their account.` : ''}
        confirmLabel="Unlink"
        cancelLabel="Keep them"
        destructive
        icon={<Trash2 className="w-6 h-6" strokeWidth={2.2} />}
        onConfirm={handleUnlink}
        loading={!!(unlinkTarget && busyId === unlinkTarget.id)}
        testId="unlink-farmer"
      />
    </div>
  );
}
